import AsyncStorage from '@react-native-async-storage/async-storage';
import { getQueue } from './requestQueue';
import { getNetworkStatus } from './networkManager';
import { probeServerReachable } from './serverReachability';
import { buildSessionScopeId, getSessionScopeIdFromStore } from './sessionScope';
import { isOfflineModeEntitled } from './offlineEntitlement';

const PENDING_CONTEXT_SWITCH_KEY = 'pendingSessionContextSwitch';

export type PendingSessionContextSwitch = {
	companyNumber?: string | null;
	targetUserId?: string | null;
	sessionScopeId: string;
	requestedAt: number;
};

export async function getPendingSessionContextSwitch(): Promise<PendingSessionContextSwitch | null> {
	try {
		const raw = await AsyncStorage.getItem(PENDING_CONTEXT_SWITCH_KEY);
		return raw ? JSON.parse(raw) : null;
	} catch {
		return null;
	}
}

export async function setPendingSessionContextSwitch(
	payload: PendingSessionContextSwitch
): Promise<void> {
	await AsyncStorage.setItem(PENDING_CONTEXT_SWITCH_KEY, JSON.stringify(payload));
}

export async function clearPendingSessionContextSwitch(): Promise<void> {
	await AsyncStorage.removeItem(PENDING_CONTEXT_SWITCH_KEY);
}

/** Count queued mutations for a given session scope (or current if omitted). */
export async function countQueuedMutationsForScope(
	sessionScopeId?: string
): Promise<number> {
	const scope = sessionScopeId ?? getSessionScopeIdFromStore();
	const queue = await getQueue();
	return queue.filter(
		(item) => (item.sessionScopeId ?? 'unknown') === scope
	).length;
}

export type CompanySwitchReadiness = {
	canSwitchOnline: boolean;
	canSwitchOfflineLocally: boolean;
	pendingForCurrentScope: number;
	reason?: 'offline' | 'server_unreachable' | 'pending_sync';
};

/**
 * Whether company/user context can be switched now.
 * Live switch requires server; offline allows local UI switch if entitled + cached data exists.
 */
export async function assessCompanySwitchReadiness(): Promise<CompanySwitchReadiness> {
	const online = getNetworkStatus();
	const serverUp = online ? await probeServerReachable() : false;
	const pendingForCurrentScope = await countQueuedMutationsForScope();

	if (online && serverUp) {
		return {
			canSwitchOnline: true,
			canSwitchOfflineLocally: false,
			pendingForCurrentScope,
		};
	}

	return {
		canSwitchOnline: false,
		canSwitchOfflineLocally: isOfflineModeEntitled(),
		pendingForCurrentScope,
		reason: online ? 'server_unreachable' : 'offline',
	};
}

export function resolveScopeForSwitch(options: {
	companyNumber?: string | null;
	targetUserId?: string | null;
	userId?: string | null;
}): string {
	return buildSessionScopeId({
		companyId: options.companyNumber,
		targetUserId: options.targetUserId,
		userId: options.userId,
	});
}

/** Apply a deferred server context switch before replaying scoped offline mutations. */
export async function applyPendingSessionContextSwitch(): Promise<boolean> {
	const pending = await getPendingSessionContextSwitch();
	if (!pending) return false;

	try {
		const { switchCompany } = await import('../api');
		await switchCompany({
			companyNumber: pending.companyNumber ?? null,
			targetUserId: pending.targetUserId ?? null,
		});
		await clearPendingSessionContextSwitch();
		return true;
	} catch (error) {
		if (__DEV__) {
			console.warn('[companyContextSwitch] pending switch failed', error);
		}
		return false;
	}
}

/**
 * Offline-only: record intended context switch for when the server is reachable again.
 * Caller must update Redux (SWITCH_COMPANY_REQUEST) and hydrate cached queries locally.
 */
export async function queuePendingSessionContextSwitch(options: {
	companyNumber?: string | null;
	targetUserId?: string | null;
	userId?: string | null;
}): Promise<void> {
	await setPendingSessionContextSwitch({
		companyNumber: options.companyNumber ?? null,
		targetUserId: options.targetUserId ?? null,
		sessionScopeId: resolveScopeForSwitch(options),
		requestedAt: Date.now(),
	});
}
